import MobileDetect from 'mobile-detect';

import adroll from './adroll';
import facebook from './facebook';
import googleAdwords from './google-adwords';
import googleAnalytics from './google-analytics';
import perfectAudience from './perfect-audience';
import pinterest from './pinterest';
import twitter from './twitter';
import custom from './custom';
import vwo from './vwo';
import klaviyo from './klaviyo';
import snapchat from './snapchat';
import tiktok from './tiktok';
import logger from '../../utils/logger';
import config from 'config';

class DataCollection {
  constructor() {
    this.md = new MobileDetect(window.navigator.userAgent);
  }

  _init(vuex, productId, productSku, color, price) {
    if (productId) {
      this._updateState(vuex, 'product', this._productPayload(productId, productSku, color, price));
    }

    if (vuex.rootState.sales.campaign.products.length) {
      this._updateState(vuex, 'campaign', this._campaignPayload(vuex.rootState.sales.campaign, vuex.rootState.isWhitelabel));
    }
  }

  _updateState({ state, commit }, property, value) {
    commit('updateState', { object: state, property, value });
  }

  _campaignPayload(campaign, isWhitelabel) {
    let urlSplit = campaign.url.split('/');

    return {
      analytics_cid: `${campaign.analytics_cid}`,
      category: campaign.category,
      url: isWhitelabel ? urlSplit[urlSplit.length - 1] : campaign.url.substring(1),
      slug: isWhitelabel ? urlSplit[urlSplit.length - 1] : urlSplit[2],
      analytics_uid: `${campaign.analytics_uid}`,
      name: `${campaign.name}`,
      tracking: campaign.tracking,
    };
  }

  _productPayload(id, name, variant, price) {
    let payload = {
      id: `${id}`,
      name: `${name}`,
      variant: `${variant}`,
      price: `${price.toFixed(2)}`,
    };

    return payload;
  }

  async _wlGoogleAnalytics(vuex, eventName) {
    let response;

    if (vuex.rootState.isWhitelabel) {
      let wlId = vuex.rootState.domainConfig.meta.ga_code;

      if (wlId) {
        response = await googleAnalytics[eventName](vuex, wlId);
      } else {
        logger.log('WL Google Analytics not configured.');

        response = Promise.resolve();
      }
    } else {
      response = Promise.resolve();
    }

    return response;
  }

  get isBot() {
    return this.md.is('bot') || window.location.search.includes('_escaped_fragment_') || navigator.userAgent.indexOf('HeadlessChrome') !== -1;
  }

  identify(vuex) {
    if (!this.isBot && !vuex.rootState.isWhitelabel) {
      logger.groupCollapsed('Identify');

      if (config.KLAVIYO_ID) {
        klaviyo.identify(vuex);
      }

      logger.groupEnd();
    }
  }

  async viewPage(vuex, excludeNonGa) {
    if (!this.isBot) {
      logger.groupCollapsed('View Page');

      if (excludeNonGa) {
        googleAnalytics.viewPage(vuex);
      } else {
        facebook.viewPage(vuex);

        if (!vuex.rootState.isWhitelabel) {
          googleAdwords.viewPage(vuex);

          snapchat.viewPage(vuex);

          tiktok.viewPage(vuex);

          adroll.trackVS();
        }

        await googleAnalytics.viewPage(vuex);

        await this._wlGoogleAnalytics(vuex, 'viewPage');
      }

      logger.groupEnd();
    }
  }

  async applyCoupon(vuex, coupon) {
    if (!this.isBot) {
      logger.groupCollapsed('Event Apply Coupon');

      this._updateState(vuex, 'coupon', coupon);

      await googleAnalytics.vsApplyCoupon(vuex);

      await this._wlGoogleAnalytics(vuex, 'vsApplyCoupon');

      this._updateState(vuex, 'coupon', null);

      logger.groupEnd();
    }
  }

  async viewCollection(vuex) {
    if (!this.isBot) {
      logger.groupCollapsed('View Collection Page');

      //- vs tracking
      facebook.viewPage(vuex);

      if (!vuex.rootState.isWhitelabel) {
        googleAdwords.viewPage(vuex);

        snapchat.viewPage();

        adroll.trackVS();

        tiktok.viewPage(vuex);
      }

      //- client tracking
      facebook.clientViewCollection(vuex);

      googleAnalytics.clientViewCollection(vuex);

      tiktok.clientViewCollection(vuex);

      // vs google analytics
      await googleAnalytics.viewPage(vuex);

      await this._wlGoogleAnalytics(vuex, 'viewPage');

      logger.groupEnd();
    }
  }

  async viewSales(vuex, { selectedProduct, color, price }) {
    if (!this.isBot) {
      logger.groupCollapsed('View Sales Page');

      this._init(vuex, selectedProduct.id, selectedProduct.sku, color.name, price);

      // vs tracking
      facebook.vsViewSales(vuex);

      if (!vuex.rootState.isWhitelabel) {
        googleAdwords.vsViewSales(vuex);

        vwo.vsViewSales(vuex);

        snapchat.viewSales(vuex);

        adroll.trackVS();

        tiktok.viewSales(vuex);
      }

      if (config.KLAVIYO_ID) {
        klaviyo.vsViewSales(vuex.rootState, selectedProduct, color, price);
      }

      // client tracking
      facebook.clientViewSales(vuex);

      googleAnalytics.clientViewSales(vuex);

      twitter.clientViewSales(vuex);

      adroll.clientViewSales(vuex);

      googleAdwords.clientViewSales(vuex);

      perfectAudience.clientViewSales(vuex);

      pinterest.clientViewSales(vuex);

      tiktok.clientViewSales(vuex);

      // vs google analytics
      await googleAnalytics.vsViewSales(vuex);

      await this._wlGoogleAnalytics(vuex, 'vsViewSales');

      logger.groupEnd();
    }
  }

  async viewThankYou(vuex) {
    if (!this.isBot) {
      logger.groupCollapsed('View Thank You Page');

      this._updateState(vuex, 'order', vuex.rootState.orderSummary.orderGroup);

      // vs tracking
      facebook.vsViewThankYou(vuex);

      if (!vuex.rootState.isWhitelabel) {
        googleAdwords.vsViewThankYou(vuex);

        vwo.vsViewThankYou(vuex);

        snapchat.viewThankYou(vuex);

        adroll.trackVS();

        tiktok.viewThankYou(vuex);
      }

      // client tracking
      facebook.clientViewThankYou(vuex);

      googleAdwords.clientViewThankYou(vuex);

      googleAnalytics.clientViewThankYou(vuex);

      adroll.clientViewThankYou(vuex);

      perfectAudience.clientViewThankYou(vuex);

      custom.clientViewThankYou(vuex);

      twitter.clientViewThankYou(vuex);

      pinterest.clientViewThankYou(vuex);

      tiktok.clientViewThankYou(vuex);

      // vs google analytics
      await googleAnalytics.vsViewThankYou(vuex);

      await this._wlGoogleAnalytics(vuex, 'vsViewThankYou');

      logger.groupEnd();
    }
  }

  async addToCart(vuex, { selectedProduct, size, quantity, color, price, cpid }) {
    if (!this.isBot) {
      logger.groupCollapsed('Event Add to Cart');

      this._init(vuex, selectedProduct.id, selectedProduct.sku, color, price);

      this._updateState(vuex, 'quantity', quantity);

      this._updateState(vuex, 'size', size);

      // vs tracking
      facebook.vsAddToCart(vuex);

      if (config.KLAVIYO_ID) {
        klaviyo.vsAddToCart(vuex.rootState, selectedProduct, price, size, quantity, cpid);
      }

      if (!vuex.rootState.isWhitelabel) {
        snapchat.addToCart(vuex);

        adroll.addToCart(vuex);

        tiktok.addToCart(vuex);
      }

      // client tracking
      facebook.clientAddToCart(vuex);

      pinterest.clientAddToCart(vuex);

      twitter.clientAddToCart(vuex);

      tiktok.clientAddToCart(vuex);

      // vs google analytics
      await googleAnalytics.vsAddToCart(vuex);

      await this._wlGoogleAnalytics(vuex, 'vsAddToCart');

      this._updateState(vuex, 'quantity', null);

      this._updateState(vuex, 'size', null);

      logger.groupEnd();
    }
  }

  async changeColor(vuex, { selectedProduct, color, price }) {
    if (!this.isBot) {
      logger.groupCollapsed('Event Change Color');

      this._init(vuex, selectedProduct.id, selectedProduct.sku, color.name, price);

      await googleAnalytics.vsChangeColor(vuex);

      await this._wlGoogleAnalytics(vuex, 'vsChangeColor');

      logger.groupEnd();
    }
  }

  async changeQuantity(vuex, { selectedProduct, color, price, quantity }) {
    if (!this.isBot) {
      logger.groupCollapsed('Event Change Quantity');

      this._init(vuex, selectedProduct.id, selectedProduct.sku, color.name, price);

      this._updateState(vuex, 'quantity', quantity);

      await googleAnalytics.vsChangeQuantity(vuex);

      await this._wlGoogleAnalytics(vuex, 'vsChangeQuantity');

      this._updateState(vuex, 'quantity', null);

      logger.groupEnd();
    }
  }

  async changeSize(vuex, { selectedProduct, color, price, size }) {
    if (!this.isBot) {
      logger.groupCollapsed('Event Change Size');

      this._init(vuex, selectedProduct.id, selectedProduct.sku, color.name, price);

      this._updateState(vuex, 'size', size);

      await googleAnalytics.vsChangeSize(vuex);

      await this._wlGoogleAnalytics(vuex, 'vsChangeSize');

      this._updateState(vuex, 'size', null);

      logger.groupEnd();
    }
  }

  async removeFromCart(vuex, item) {
    if (!this.isBot) {
      logger.groupCollapsed('Event Remove from Cart');

      this._init(vuex, item.product_id, item.product_sku, item.color, item.price_per_unit);

      this._updateState(vuex, 'quantity', item.quantity);

      this._updateState(vuex, 'size', item.size);

      await googleAnalytics.vsRemoveFromCart(vuex);

      await this._wlGoogleAnalytics(vuex, 'vsRemoveFromCart');

      this._updateState(vuex, 'quantity', null);

      this._updateState(vuex, 'size', null);

      logger.groupEnd();
    }
  }

  async initiateCheckout(vuex) {
    if (!this.isBot) {
      logger.groupCollapsed('Event Initiate Checkout');

      this._updateState(vuex, 'cart', vuex.rootState.cart.cart);

      // vs tracking
      facebook.vsInitiateCheckout(vuex);

      if (config.KLAVIYO_ID) {
        klaviyo.vsInitiateCheckout(vuex);
      }

      if (!vuex.rootState.isWhitelabel) {
        snapchat.initiateCheckout(vuex);

        tiktok.initiateCheckout(vuex);
      }

      // client tracking
      facebook.clientInitiateCheckout(vuex);

      twitter.clientInitiateCheckout(vuex);

      tiktok.clientInitiateCheckout(vuex);

      // vs google analytics
      await googleAnalytics.vsInitiateCheckout(vuex);

      await this._wlGoogleAnalytics(vuex, 'vsInitiateCheckout');

      logger.groupEnd();
    }
  }

  async completeOrder(vuex) {
    if (!this.isBot) {
      logger.groupCollapsed('Event Place Order');

      this._updateState(vuex, 'cart', vuex.rootState.cart.cart);

      if (!vuex.rootState.isWhitelabel) {
        vwo.vsCompleteOrder(vuex);

        tiktok.completeOrder(vuex);
      }

      tiktok.clientCompleteOrder(vuex);

      await googleAnalytics.vsCompleteOrder(vuex);

      await this._wlGoogleAnalytics(vuex, 'vsCompleteOrder');

      logger.groupEnd();
    }
  }

  async initiateCheckoutUpsell(vuex) {
    if (!this.isBot) {
      logger.groupCollapsed('Event Initiate Checkout Upsell');

      this._updateState(vuex, 'upsell', vuex.rootState.thankYou.currentUpsell);

      await googleAnalytics.vsInitiateCheckoutUpsell(vuex);

      await this._wlGoogleAnalytics(vuex, 'vsInitiateCheckoutUpsell');

      logger.groupEnd();
    }
  }

  async completeOrderUpsell(vuex) {
    if (!this.isBot) {
      logger.groupCollapsed('Event Purchase Upsell');

      this._updateState(vuex, 'upsell', vuex.rootState.thankYou.currentUpsell);

      this._updateState(vuex, 'order', vuex.rootState.orderSummary.orderGroup);

      googleAnalytics.clientCompleteOrderUpsell(vuex);

      await googleAnalytics.vsCompleteOrderUpsell(vuex);

      await this._wlGoogleAnalytics(vuex, 'vsCompleteOrderUpsell');

      logger.groupEnd();
    }
  }

  register(vuex) {
    if (!this.isBot) {
      logger.groupCollapsed('Event Register');

      facebook.vsRegister();

      googleAdwords.vsRegister();

      snapchat.register();

      tiktok.register(vuex);

      logger.groupEnd();
    }
  }

  login(vuex) {
    if (!this.isBot) {
      logger.groupCollapsed('Event Login');

      snapchat.login();

      logger.groupEnd();
    }
  }

  search(vuex) {
    if (!this.isBot) {
      logger.groupCollapsed('Event Search');

      snapchat.search(vuex);

      tiktok.search(vuex);

      logger.groupEnd();
    }
  }

  async continueShopping(vuex) {
    if (!this.isBot) {
      logger.groupCollapsed('Continue Shopping');

      await googleAnalytics.vsContinueShopping(vuex);

      await this._wlGoogleAnalytics(vuex, 'vsContinueShopping');

      logger.groupEnd();
    }
  }
};

export default new DataCollection();